// STUDIO-BLUETOOTH-REMOTE-INPUT-DIAG-1/-POINTER-DIAG-2 · testmode 전용 리모컨 입력 확인 패널 (전체 화면 덮개 · 로컬 메모리만).
import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Gamepad2, X } from 'lucide-react';
import useRemoteInputLog from '@/hooks/useRemoteInputLog';
import { SEGMENTS, segLabel, summarize, summarizeSegments, toText, fmtPointer } from '@/lib/remoteInputDiag';

const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); return true; } catch {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy'); document.body.removeChild(ta); return ok;
  }
};

// 리모컨 Enter/Space 가 패널 버튼을 누르지 않도록 포커스 제거.
const noFocus = (fn) => (e) => { e.currentTarget.blur(); fn(e); };
const label = (k) => (k === ' ' ? 'Space(" ")' : k || '-');
const modsTxt = (m) => ['ctrl', 'alt', 'shift', 'meta'].filter((k) => m[k]).map((k) => k[0].toUpperCase() + k.slice(1)).join('+');
const kv = (o) => Object.entries(o).map(([k, v]) => `${k}:${v}`).join(', ') || '-';
const isKey = (r) => r.type === 'keydown' || r.type === 'keyup';

const Btn = ({ children, testId, onClick, tone = 'gray', active = false }) => (
  <button type="button" tabIndex={-1} data-diag-control onClick={noFocus(onClick)} data-testid={testId}
          className={`min-h-[36px] px-3 rounded-full text-xs font-black transition-colors ${tone === 'red'
            ? 'bg-[#FF3B30] text-white' : active ? 'bg-[#FFD60A] text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}>
    {children}
  </button>
);

const SegmentPicker = ({ segment, setSegment }) => (
  <div className="flex flex-wrap gap-1.5" data-diag-control data-testid="remote-diag-segments">
    <span className="text-[11px] text-white/50 self-center mr-1">테스트 구간</span>
    {SEGMENTS.map((s) => (
      <Btn key={s.id} testId={`remote-diag-seg-${s.id}`} active={segment === s.id} onClick={() => setSegment(s.id)}>{s.label}</Btn>
    ))}
  </div>
);

const LastInput = ({ r }) => (
  <div className="rounded-2xl bg-white/5 border border-white/10 p-3" data-testid="remote-diag-last">
    <p className="text-[11px] text-white/50">마지막 입력</p>
    {r ? (
      <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-sm font-mono text-white">
        <span>type: <b data-testid="remote-diag-last-type">{r.type}</b></span>
        <span>구간: <b data-testid="remote-diag-last-seg">{segLabel(r.seg)}</b></span>
        {isKey(r) ? (
          <>
            <span className="col-span-2 break-all">key: <b className="text-[#FFD60A]" data-testid="remote-diag-last-key">{label(r.key)}</b> · repeat: <b data-testid="remote-diag-last-repeat">{String(r.repeat)}</b></span>
            <span className="col-span-2 break-all">code: <b className="text-[#FFD60A]" data-testid="remote-diag-last-code">{r.code || '-'}</b></span>
          </>
        ) : (
          <span className="col-span-2 break-all text-[#FFD60A] text-xs" data-testid="remote-diag-last-pointer">{fmtPointer(r) || r.key || '-'}</span>
        )}
        <span className="col-span-2 text-[11px] text-white/60" data-testid="remote-diag-last-meta">
          +{r.atMs}ms · 간격 {r.gapMs ?? '-'}ms · 대상 {r.target}{r.heldMs != null ? ` · 누름 ${r.heldMs}ms` : ''}
          {modsTxt(r.mods) ? ` · ${modsTxt(r.mods)}` : ''}{r.risky ? ' · 주의(시스템/브라우저 동작 가능)' : ''}
        </span>
      </div>
    ) : <p className="mt-1 text-sm text-white/60" data-testid="remote-diag-empty">구간을 고른 뒤 리모컨 버튼을 눌러 보세요.</p>}
  </div>
);

const Summary = ({ records }) => {
  const segs = summarizeSegments(records);
  const keys = summarize(records);
  if (!segs.length) return null;
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-3 max-h-[30vh] overflow-y-auto" data-testid="remote-diag-summary">
      <p className="text-[11px] text-white/50 mb-1">구간별 비교 (같은 결과면 버튼 구분 불가)</p>
      {segs.map((s) => (
        <p key={s.seg} className="text-[11px] font-mono text-white break-all mb-1" data-testid="remote-diag-seg-row">
          <b className="text-[#FFD60A]">[{s.label}]</b> {s.count}건 · {kv(s.types)} · 포인터 {kv(s.pointerTypes)} · btn {kv(s.buttons)} ·
          방향 {kv(s.dirs)} · x {s.xRange ? s.xRange.join('~') : '-'} · y {s.yRange ? s.yRange.join('~') : '-'} · 간격 {s.avgGapMs ?? '-'}ms
          {Object.keys(s.keys).length ? ` · 키 ${kv(s.keys)}` : ''}
        </p>
      ))}
      {keys.map((s) => (
        <p key={`${s.key}|${s.code}`} className="text-[11px] font-mono text-white/80 break-all" data-testid="remote-diag-summary-row">
          {label(s.key)} / {s.code} · {s.down} · {s.repeat} · {s.up} · {s.maxHeldMs}ms{s.risky ? ' · 주의' : ''}
        </p>
      ))}
    </div>
  );
};

const Log = ({ records, scroll }) => (
  <div className={`flex-1 min-h-[120px] rounded-2xl bg-black/40 border border-white/10 p-2 ${scroll ? 'overflow-y-auto' : 'overflow-hidden'}`}
       style={{ touchAction: scroll ? 'pan-y' : 'none' }} data-testid="remote-diag-log">
    {records.map((r) => (
      <p key={r.seq} className={`text-[11px] font-mono break-all ${r.risky ? 'text-[#FF9F0A]' : r.dir ? 'text-[#64D2FF]' : r.type.endsWith('up') ? 'text-white/50' : 'text-white/90'}`}
         data-testid="remote-diag-log-row">
        #{r.seq} [{segLabel(r.seg)}] +{r.atMs}ms Δ{r.gapMs ?? '-'} {r.type}{' '}
        {isKey(r) ? `${label(r.key)} ${r.code}${r.repeat ? ' repeat' : ''}` : fmtPointer(r) || r.key}
        {modsTxt(r.mods) ? ` [${modsTxt(r.mods)}]` : ''}{r.heldMs != null ? ` ${r.heldMs}ms` : ''} → {r.target}
      </p>
    ))}
  </div>
);

export const RemoteInputDiagPanel = ({ onClose }) => {
  const panelRef = useRef(null);
  const [blockDefault, setBlockDefault] = useState(true);
  const [segment, setSegment] = useState('none');
  const [logScroll, setLogScroll] = useState(false);
  const { records, pageState, hoverMoves, pen, clear } = useRemoteInputLog(true, { blockDefault, panelRef, segment });
  const penTxt = `눌린 채 남은 펜 ${pen.active.length}개${pen.active.length ? `(id ${pen.active.join(',')})` : ''} · 마지막 펜 이후 손가락 ${pen.fingerAfterPen}회 · touchstart ${pen.touchStarts}회 · 최대 터치 ${navigator.maxTouchPoints}`;
  const copy = async () => {
    const ok = await copyText(toText(records, { ua: navigator.userAgent, pen: penTxt, screen: `${window.innerWidth}x${window.innerHeight} 화면 ${window.screen.width}x${window.screen.height} dpr=${window.devicePixelRatio}` }));
    if (ok) toast.success('입력 기록을 복사했습니다'); else toast.error('복사에 실패했습니다');
  };
  return (
    <div ref={panelRef} className="fixed inset-0 z-[80] bg-[#0B0B0C]/95 backdrop-blur-md p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))] select-none"
         style={{ touchAction: 'none', overscrollBehavior: 'none' }} data-testid="remote-diag-panel">
      <div className="h-full w-full max-w-2xl mx-auto flex flex-col gap-2.5">
        <div className="flex items-center gap-2">
          <Gamepad2 size={18} className="text-[#FF3B30]" />
          <p className="text-sm font-black text-white">리모컨 입력 확인</p>
          <span className="ml-1 px-2 py-0.5 rounded-full bg-[#30D158]/20 text-[#30D158] text-[10px] font-black" data-testid="remote-diag-status">진단 ON</span>
          <button type="button" tabIndex={-1} data-diag-control onClick={noFocus(onClose)} className="ml-auto p-1.5 text-white/70"
                  aria-label="진단 종료" data-testid="remote-diag-close-x"><X size={18} /></button>
        </div>
        <p className="text-[11px] text-white/60" data-testid="remote-diag-page-state">
          포커스 {pageState.focus ? 'ON' : 'OFF'} · 화면 {pageState.visible ? '보임' : '숨김'} · 기록 {records.length}건 ·
          누르지 않은 이동 {hoverMoves}회 · 상품·캡처·LIVE 동작 없음 · 서버 저장 없음
        </p>
        <p className={`text-[11px] font-mono ${pen.active.length ? 'text-[#FF9F0A]' : 'text-white/60'}`} data-testid="remote-diag-pen-state">{penTxt}</p>
        <SegmentPicker segment={segment} setSegment={setSegment} />
        <LastInput r={records[0]} />
        <Summary records={records} />
        <Log records={records} scroll={logScroll} />
        <label className="flex items-center gap-2 text-[11px] text-white/70" data-diag-control>
          <input type="checkbox" tabIndex={-1} checked={blockDefault} onChange={(e) => { setBlockDefault(e.target.checked); e.target.blur(); }}
                 data-testid="remote-diag-block-default" />
          브라우저 기본 동작 막기 (끄면 스크롤·뒤로가기 등 기본 동작을 관찰)
        </label>
        <label className="flex items-center gap-2 text-[11px] text-white/70" data-diag-control>
          <input type="checkbox" tabIndex={-1} checked={logScroll} onChange={(e) => { setLogScroll(e.target.checked); e.target.blur(); }}
                 data-testid="remote-diag-log-scroll" />
          기록 손가락 스크롤 허용 (켜면 방향 버튼 경로가 스크롤에 끊길 수 있음)
        </label>
        <div className="flex flex-wrap gap-2">
          <Btn testId="remote-diag-clear" onClick={clear}>입력 기록 초기화</Btn>
          <Btn testId="remote-diag-copy" onClick={copy}>결과 복사</Btn>
          <Btn testId="remote-diag-close" tone="red" onClick={onClose}>진단 종료</Btn>
        </div>
      </div>
    </div>
  );
};

export default RemoteInputDiagPanel;
